 // components/TourPlanningSection.js
import React, { useState } from 'react';
import { Loader, Wand2, ChevronDown, ChevronUp } from 'lucide-react';
import VoiceSelector from '../../selectors/ToneOfVoiceSelector';
import ModelSelector from '../../selectors/ModelSelector';

// Target audience options
const TARGET_AUDIENCES = [
  "Solo travelers",
  "Couples",
  "Families with children",
  "Teenagers",
  "Young adults",
  "Senior travelers",
  "History enthusiasts",
  "Art lovers",
  "Food connoisseurs",
  "Adventure seekers",
  "Architecture buffs",
  "Photography enthusiasts",
  "Nature lovers",
  "Culture explorers",
  "Budget travelers",
  "Luxury travelers",
  "First-time visitors",
  "Repeat visitors",
  "Business travelers",
  "Special needs travelers"
];

 
const TourPlanningSection = ({ 
  planningData,
  onPlanningChange,
  onGenerateTourPlan,
  isTourGenerating,
  error,
  showPrompts,
  setShowPrompts,
  voiceStyle,
  onVoiceChange,
  selectedModel,
  onModelChange
}) => {
  // State for multi-select dropdown
  const [isAudienceDropdownOpen, setIsAudienceDropdownOpen] = useState(false);
  
 
  
  // Helpers for the audience selection
  const handleAudienceToggle = (audience) => {
    const currentAudiences = planningData.targetAudience ? planningData.targetAudience.split(', ') : [];
    
    if (currentAudiences.includes(audience)) {
      // Remove the audience if already selected
      const newAudiences = currentAudiences.filter(a => a !== audience);
      onPlanningChange('targetAudience', newAudiences.join(', '));
    } else {
      // Add the audience if not selected
      const newAudiences = [...currentAudiences, audience];
      onPlanningChange('targetAudience', newAudiences.join(', '));
    }
  };
  
  // Helper to check if an audience is selected
  const isAudienceSelected = (audience) => {
    const currentAudiences = planningData.targetAudience ? planningData.targetAudience.split(', ') : [];
    return currentAudiences.includes(audience);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Wand2 className="w-5 h-5" />
          Tour Planning
        </h2>
        
        <div className="space-y-4">
          {/* AI Model Selector */}
          <ModelSelector 
            selectedModel={selectedModel}
            onModelChange={onModelChange}
          />
          
          {/* Voice Style Selector */}
          <VoiceSelector 
            selectedVoice={voiceStyle}
            onVoiceChange={onVoiceChange}
          />

          {/* Separate City and Country fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">City*</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="Enter city"
                value={planningData.city || ''}
                onChange={(e) => onPlanningChange('city', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Country*</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="Enter country"
                value={planningData.country || ''}
                onChange={(e) => onPlanningChange('country', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Theme*</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="e.g., History, Art, Food, Hidden Gems"
              value={planningData.theme || ''}
              onChange={(e) => onPlanningChange('theme', e.target.value)}
            />
          </div>

          {/* Multi-select dropdown for Target Audience */}
          <div>
            <label className="block text-sm font-medium mb-1">Target Audience*</label>
            <div className="relative">
              <div 
                className="w-full p-2 border rounded bg-white flex flex-wrap gap-1 cursor-pointer min-h-[42px]"
                onClick={() => setIsAudienceDropdownOpen(!isAudienceDropdownOpen)}
              >
                {planningData.targetAudience ? 
                  planningData.targetAudience.split(', ').map(audience => (
                    <span key={audience} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {audience}
                    </span>
                  )) : 
                  <span className="text-gray-400">Select target audiences</span>
                }
                <span className="ml-auto">
                  {isAudienceDropdownOpen ? 
                    <ChevronUp className="w-4 h-4" /> : 
                    <ChevronDown className="w-4 h-4" />
                  }
                </span>
              </div>
              
              {isAudienceDropdownOpen && (
                <div className="absolute z-10 w-full bg-white border mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {TARGET_AUDIENCES.map(audience => (
                    <div 
                      key={audience}
                      className={`p-2 hover:bg-gray-100 cursor-pointer ${isAudienceSelected(audience) ? 'bg-blue-50' : ''}`}
                      onClick={() => handleAudienceToggle(audience)}
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isAudienceSelected(audience)}
                          onChange={() => {}} // handled by the parent div's onClick
                          className="mr-2"
                        />
                        {audience}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">Click on an option to select/deselect</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Number of Stops (Optional)</label>
            <input
              type="number"
              min="1"
              max="22"
              className="w-full p-2 border rounded"
              value={planningData.numberOfStops || ''}
              onChange={(e) => onPlanningChange('numberOfStops', parseInt(e.target.value))}
              placeholder="Leave empty for flexible number of stops"
            />
          </div>
 

          <button
            onClick={onGenerateTourPlan}
            disabled={isTourGenerating || !planningData.city || !planningData.country || !planningData.theme || !planningData.targetAudience}
            className="w-full bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:bg-gray-400 flex items-center gap-2 justify-center"
          >
            {isTourGenerating ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Generating Tour Plan...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Generate Tour Plan with {selectedModel}
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  );
};

export default TourPlanningSection;